import styled from 'styled-components';
import {
  adjustColor,
  BRIGHTNESS_THRESHOLD,
  getColorBrightness
} from '../../functions/primitiveHelpers/colorHelpers';
import THEME, { COLOR } from '../../styles/colors';
import { boxModelStyles, IBoxModelStyleProps } from '../../styles/css';

export enum ButtonFill {
  Default = 0,
  Fill = 0,
  Outline = 1,
  Link = 2
}

export enum ButtonSize {
  Default = 1,
  Small = 0,
  Medium = 1,
  Large = 2
}

export enum ButtonTheme {
  Default = 0,
  MaxPreps = 0,
  Success = 1,
  Danger = 2,
  Warning = 3,
  Info = 4,
  Theme = 5,
  White = 6,
  Link = 7,
  Dismiss = 8
}

// padding is adjusted to manage the fact we will ALWAYS have borders (yes, they maybe be transparent)
export const SIZE_STYLES = new Map([
  [
    ButtonSize.Small,
    ` padding: 3px 15px;
      font-size: 11px;
      line-height: 1.8;
    `
  ],
  [
    ButtonSize.Medium,
    ` padding: 5px 15px;
      font-size: 12px;
      line-height: 1.65;
    `
  ],
  [
    ButtonSize.Large,
    ` padding: 7px 17px;
      font-size: 14px;
      line-height: 1.5;
    `
  ]
]);

const getTextColor = (color, fillMode, colorScheme, disabled, theme) => {
  const { brightness } = getColorBrightness([color.replace('#', '')])[0];
  if (colorScheme === ButtonTheme.White) {
    return fillMode === ButtonFill.Fill
      ? theme?.colors?.school?.primary ?? COLOR.black
      : COLOR.white;
  }
  if (fillMode === ButtonFill.Fill) {
    return brightness > BRIGHTNESS_THRESHOLD
      ? adjustColor(color, -60)
      : COLOR.white;
  }
  if (disabled) {
    return COLOR.neutral30;
  }
  if (brightness < BRIGHTNESS_THRESHOLD) {
    return color;
  }
  return COLOR.neutral60;
};

const getColorSyles = (c, fillMode, colorScheme, disabled, theme) => {
  const color = disabled ? COLOR.neutral0 : c;

  const hoverColor =
    fillMode === ButtonFill.Fill ? adjustColor(color, -20) : THEME.shadow.light;

  const borderColor =
    fillMode === ButtonFill.Fill ? color : adjustColor(color, -20);

  const textColor = getTextColor(color, fillMode, colorScheme, disabled, theme);

  return `
    background-color: ${color};
    border-color: ${borderColor};
    color: ${textColor};

    &:hover:not([disabled]),
    &:active:not([disabled])
    {
      background-color: ${hoverColor};
    }

    & svg, & svg *
    {
      color: ${textColor};
      fill: ${textColor};
    }
`;
};

const COLOR_MAP = new Map([
  [ButtonTheme.MaxPreps, THEME.brand.maxpreps],
  [ButtonTheme.Success, THEME.status.positive],
  [ButtonTheme.Danger, THEME.status.negative],
  [ButtonTheme.Warning, THEME.status.warning],
  [ButtonTheme.Info, COLOR.neutral95],
  [ButtonTheme.Theme, THEME.brand.maxpreps],
  [ButtonTheme.White, COLOR.white],
  [ButtonTheme.Link, THEME.other.link],
  [ButtonTheme.Dismiss, THEME.text.unselected]
]);

const FILL_STYLES = new Map([
  [ButtonFill.Fill, ``],
  [ButtonFill.Outline, `background: transparent;`],
  [ButtonFill.Link, `background: transparent; border-color: transparent;`]
]);

const getFillModeStyles = fillMode => FILL_STYLES.get(fillMode);

const getColorSchemeStyles = (
  colorScheme,
  fillMode,
  disabled = false,
  theme: any = null
) =>
  getColorSyles(
    colorScheme === ButtonTheme.Theme
      ? theme.colors.school.primary || THEME.brand.maxpreps
      : COLOR_MAP.get(colorScheme),
    fillMode,
    colorScheme,
    disabled,
    theme
  );
const getSizeStyles = size => SIZE_STYLES.get(size);

export interface IButtonProps extends IBoxModelStyleProps {
  $fillMode?: ButtonFill;
  $colorScheme?: ButtonTheme;
  $size?: ButtonSize;
  type?: string;
  as?: string;
  // TODO figure out proper way to inherit these
  href?: any;
  rel?: string;
  disabled?: boolean;
  className?: string;
  onClick?: any;
  target?: string;
}

const getType = (props: IButtonProps) => {
  const { type, as } = props;
  if (type) return type;
  if (typeof as !== 'undefined' && as !== 'button') return undefined;
  return 'button';
};

const StyledButton: any = styled.button.attrs((props: IButtonProps) => ({
  $fillMode: props.$fillMode ?? ButtonFill.Default,
  $colorScheme: props.$colorScheme ?? ButtonTheme.Default,
  $size: props.$size ?? ButtonSize.Default,
  type: getType(props)
}))`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};

  border-radius: 8px;
  border: 1px solid transparent;

  text-decoration: none;
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1.8;
  text-align: center;
  letter-spacing: 1px;

  appearance: none;

  & > svg:first-child:not(:only-child) {
    margin: 0 8px 0 0;
  }

  & > svg:last-child:not(:only-child) {
    margin: 0 0 0 8px;
  }

  ${boxModelStyles}

  ${props =>
    getColorSchemeStyles(
      props.$colorScheme,
      props.$fillMode,
      props.disabled,
      props.theme
    )}
  ${props => getSizeStyles(props.$size)}
  ${props => getFillModeStyles(props.$fillMode)}
`;

export default StyledButton;
